import { create } from 'zustand'
import {
  projectMemberApi,
  type AddProjectMemberRequest,
  type UpdateProjectMemberRequest,
} from '@/api/projectMemberApi'
import type { ProjectMember, ProjectRole } from '@/api/types'

interface ProjectMembersStore {
  members: ProjectMember[]
  myProjectRole: ProjectRole | null
  loading: boolean

  load: (projectId: number) => Promise<void>
  setMyProjectRole: (role: ProjectRole | null) => void
  add: (projectId: number, body: AddProjectMemberRequest) => Promise<ProjectMember>
  update: (
    projectId: number,
    memberId: number,
    body: UpdateProjectMemberRequest,
  ) => Promise<ProjectMember>
  remove: (projectId: number, memberId: number) => Promise<void>
  makeOwner: (projectId: number, memberId: number) => Promise<ProjectMember>
  reset: () => void
}

export const useProjectMembersStore = create<ProjectMembersStore>((set, get) => ({
  members: [],
  myProjectRole: null,
  loading: false,

  async load(projectId) {
    set({ loading: true })
    try {
      const page = await projectMemberApi.list(projectId, { limit: 100 })
      set({ members: page.results, loading: false })
    } catch {
      set({ loading: false })
    }
  },

  setMyProjectRole(role) {
    set({ myProjectRole: role })
  },

  async add(projectId, body) {
    const member = await projectMemberApi.add(projectId, body)
    set((s) => ({ members: [...s.members, member] }))
    return member
  },

  async update(projectId, memberId, body) {
    const updated = await projectMemberApi.update(projectId, memberId, body)
    set((s) => ({ members: s.members.map((m) => (m.id === memberId ? updated : m)) }))
    return updated
  },

  async remove(projectId, memberId) {
    await projectMemberApi.remove(projectId, memberId)
    set((s) => ({ members: s.members.filter((m) => m.id !== memberId) }))
  },

  async makeOwner(projectId, memberId) {
    const updated = await projectMemberApi.makeOwner(projectId, memberId)
    // Reload to reflect cascading role changes (old owner → ADMIN, target → OWNER)
    await get().load(projectId)
    return updated
  },

  reset() {
    set({ members: [], myProjectRole: null, loading: false })
  },
}))
